import svgIcons from '@/assets/svg/icons';
import CustomButton from '@/components/atoms/CustomButton';
import SvgIcon from '@/components/atoms/SvgIcon';
import WellnessStateCircle from '@/components/molecules/WellnessStateCircle';
import CircleFillOutWithText from '@/components/molecules/WellnessStateCircle/CircleFillOutWithText';
import {
  EMPTY_WELLNESS_DOMAINS,
  WELLNESS_DOMAINS_DISPLAY_LABELS,
  WELLNESS_SUB_DOMAINS_DISPLAY_LABELS,
} from '@/constants';
import colors from '@/theme/colors';
import { THealthDomain } from '@/types/Health.types';
import { Center, HStack, Text, VStack } from '@chakra-ui/react';
import React, { useState } from 'react';
import NoInsightsOverviewComponent from './NoInsightsOverviewComponent';
import { TDashboardInsights, TDashboardScores, WHOLENESS_TREND } from '@/types/Dashboard.types';
import { DASHBOARD_RIGHT_PANEL_WIDTH_PX } from '@/constants/dimensions';
import ArrowIconUp from '@/components/atoms/ArrowIconUp';
import ArrowIconDown from '@/components/atoms/ArrowIconDown';

export interface OverviewButtonProps {
  overviewBottomButtonOnClick: null | (() => void);
  overviewBottomButtonLabel?: string;
  overviewBottomButtonIconPath: string;
  overviewBottomButtonIconSize?: string | number;
  overviewDescription?: string;
}

interface Props extends OverviewButtonProps {
  dashboardInsights?: TDashboardInsights;
  dashboardScores?: TDashboardScores;
  wholenessRingAvatar?: string;
  avatarTooltipJSX?: JSX.Element | null;
  showCenteredTextOverlay?: boolean;
  subtitle?: string;
}

const TIME_STRING = 'in the last month';

const ITEMS_GAP_PX = '14px';

const OVERVIEW_RIGHT_SIDE_PANEL_HEIGHT = 675;

const WholenessRingWithOverview = ({
  overviewBottomButtonOnClick,
  overviewBottomButtonLabel,
  overviewBottomButtonIconPath,
  overviewBottomButtonIconSize,
  overviewDescription,
  dashboardInsights,
  dashboardScores,
  wholenessRingAvatar,
  avatarTooltipJSX,
  showCenteredTextOverlay = true,
  subtitle = 'This graph displays the total scores of your wholeness broken down per domain.',
}: Props) => {
  const [selectedDomain, setSelectedDomain] = useState<THealthDomain>();

  const perDomainInsights = dashboardInsights ? dashboardInsights?.perDomain : undefined;
  const noInsightsOverview =
    !perDomainInsights ||
    (perDomainInsights?.combined.trend === WHOLENESS_TREND.IMPROVING &&
      perDomainInsights?.combined.value === 0 &&
      Object.entries(perDomainInsights?.declining).length === 0 &&
      Object.entries(perDomainInsights?.improving).length === 0);

  const subdomainData =
    Object.entries(dashboardInsights?.perDomainSubdomains ?? {}).length > 0
      ? dashboardInsights?.perDomainSubdomains
      : undefined;
  const combinedWholenessImproving = perDomainInsights
    ? perDomainInsights.combined.trend === WHOLENESS_TREND.IMPROVING
    : undefined;

  return (
    <HStack width={'100%'} gap={'60px'} align={'start'} justifyContent={'space-between'}>
      {/* Wholeness Ring */}
      <VStack width={'60%'} gap={'10px'}>
        <Text variant="loraSectionTitle" textAlign={'start'}>
          Wholeness Ring
        </Text>
        <Text variant={'urbanistMediumRegular'} color={'text.mediumGray'} marginBottom={'25px'}>
          {subtitle}
        </Text>
        <WellnessStateCircle
          wellnessData={dashboardScores?.domains ?? EMPTY_WELLNESS_DOMAINS}
          totalScore={dashboardScores?.totalScore}
          userAvatar={wholenessRingAvatar}
          selectedDomain={selectedDomain}
          setSelectedDomain={setSelectedDomain}
          showCenteredTextOverlay={showCenteredTextOverlay}
          avatarTooltipJSX={avatarTooltipJSX}
        />
      </VStack>

      {/* Overview Right Side Panel */}
      <VStack
        width={DASHBOARD_RIGHT_PANEL_WIDTH_PX}
        height={`${OVERVIEW_RIGHT_SIDE_PANEL_HEIGHT}px`}
        bgColor={'extra.white'}
        padding={'20px'}
        borderRadius={'20px'}
        boxShadow="0px 10px 80px 0px #00417926"
        justifyContent={'space-between'}
      >
        {noInsightsOverview ? (
          <NoInsightsOverviewComponent
            overviewBottomButtonLabel={overviewBottomButtonLabel}
            overviewBottomButtonOnClick={overviewBottomButtonOnClick}
            overviewBottomButtonIconPath={overviewBottomButtonIconPath}
            overviewBottomButtonIconSize={overviewBottomButtonIconSize}
            overviewDescription={overviewDescription}
          />
        ) : (
          <>
            <VStack
              height={`${OVERVIEW_RIGHT_SIDE_PANEL_HEIGHT - 100}px`}
              width={'100%'}
              overflowY={'scroll'}
              paddingRight={'10px'}
              marginRight={'-15px'}
              paddingBottom={'50px'}
            >
              {selectedDomain ? (
                // Selected Domain
                <VStack width={'100%'} alignItems={'start'} gap={'24px'}>
                  <VStack alignItems={'start'} gap={'14px'}>
                    <HStack gap={'14px'}>
                      <Center cursor={'pointer'} onClick={() => setSelectedDomain(undefined)}>
                        <SvgIcon
                          iconPath={svgIcons.leftArrow}
                          color={colors.primary[500]}
                          size={20}
                          boxHeight={12}
                          boxWidth={18}
                        />
                      </Center>
                      <Text variant={'urbanistBold20'} color={'primary.500'}>
                        {WELLNESS_DOMAINS_DISPLAY_LABELS[selectedDomain]} Wholeness
                      </Text>
                    </HStack>
                    <Text variant={'urbanistMedium'} color={'text.mediumGray'}>
                      This is an overview of your current {selectedDomain} wholeness.
                    </Text>
                  </VStack>

                  {!subdomainData && <Text variant={'urbanistBoldLarge'}>No Data</Text>}

                  {/* Needs Immediate Attention */}
                  {subdomainData &&
                    Object.entries(subdomainData[selectedDomain as THealthDomain].immediateAttention).length && (
                      <VStack gap={'16px'} align={'start'}>
                        <Text variant={'urbanistMaximumBold12'} color={'extra.red'}>
                          NEEDS IMMEDIATE ATTENTION
                        </Text>

                        {Object.entries(subdomainData[selectedDomain as THealthDomain].immediateAttention).map(
                          ([subdomainName, subdomainValue], i) => (
                            <HStack key={subdomainName}>
                              <CircleFillOutWithText
                                percentageFilled={subdomainValue * 10}
                                svgId={`${subdomainName}-${i}`}
                                circleDiameter={20}
                                thickness={3}
                                showPercentageText={false}
                              />
                              <Text
                                variant={'urbanistBold14'}
                                color={'text.mediumGray'}
                                marginRight={'6px'}
                                marginLeft={'2px'}
                              >
                                {subdomainValue * 10}
                              </Text>
                              <Text variant={'urbanistSemiBoldRegular'}>
                                {WELLNESS_SUB_DOMAINS_DISPLAY_LABELS[subdomainName]}
                              </Text>
                            </HStack>
                          ),
                        )}
                      </VStack>
                    )}

                  {/* Developing Potential */}
                  {subdomainData &&
                    Object.entries(subdomainData[selectedDomain as THealthDomain].developingPotential).length && (
                      <VStack gap={'16px'} align={'start'}>
                        <Text variant={'urbanistMaximumBold12'} color={'extra.yellowStrong'}>
                          DEVELOPING POTENTIAL
                        </Text>

                        {Object.entries(subdomainData[selectedDomain as THealthDomain].developingPotential).map(
                          ([subdomainName, subdomainValue], i) => (
                            <HStack key={subdomainName}>
                              <CircleFillOutWithText
                                percentageFilled={subdomainValue * 10}
                                svgId={`${subdomainName}-${i}`}
                                circleDiameter={20}
                                thickness={3}
                                showPercentageText={false}
                              />
                              <Text
                                variant={'urbanistBold14'}
                                color={'text.mediumGray'}
                                marginRight={'6px'}
                                marginLeft={'2px'}
                              >
                                {subdomainValue * 10}
                              </Text>
                              <Text variant={'urbanistSemiBoldRegular'}>
                                {WELLNESS_SUB_DOMAINS_DISPLAY_LABELS[subdomainName]}
                              </Text>
                            </HStack>
                          ),
                        )}
                      </VStack>
                    )}

                  {/* Improving Areas */}
                  {subdomainData &&
                    Object.entries(subdomainData[selectedDomain as THealthDomain]?.improving).length && (
                      <VStack gap={'16px'} align={'start'}>
                        <Text variant={'urbanistMaximumBold12'}>IMPROVING AREAS</Text>

                        {Object.entries(subdomainData[selectedDomain as THealthDomain].improving).map(
                          ([subdomainName, subdomainValue], i) => (
                            <HStack key={subdomainName}>
                              <ArrowIconUp />
                              <Text variant={'urbanistSemiBoldRegular'}>
                                {WELLNESS_SUB_DOMAINS_DISPLAY_LABELS[subdomainName]} {i % 2 == 0 ? 'improved' : 'rose'}{' '}
                                by {subdomainValue}% {TIME_STRING}.
                              </Text>
                            </HStack>
                          ),
                        )}
                      </VStack>
                    )}

                  {/* Declining Areas */}
                  {subdomainData &&
                    Object.entries(subdomainData[selectedDomain as THealthDomain]?.declining).length && (
                      <VStack gap={'16px'} align={'start'}>
                        <Text variant={'urbanistMaximumBold12'}>DECLINING AREAS</Text>

                        {Object.entries(subdomainData[selectedDomain as THealthDomain].declining).map(
                          ([subdomainName, subdomainValue], i) => (
                            <HStack key={subdomainName}>
                              <ArrowIconDown />
                              <Text variant={'urbanistSemiBoldRegular'}>
                                {WELLNESS_SUB_DOMAINS_DISPLAY_LABELS[subdomainName]}{' '}
                                {i % 2 == 0 ? 'declined' : 'dropped'} by {subdomainValue}% {TIME_STRING}.
                              </Text>
                            </HStack>
                          ),
                        )}
                      </VStack>
                    )}

                  {/* Combined Wholeness */}
                  {subdomainData && subdomainData[selectedDomain as THealthDomain]?.combined && (
                    <VStack gap={'16px'} align={'start'}>
                      <Text variant={'urbanistMaximumBold12'}>
                        COMBINED {WELLNESS_DOMAINS_DISPLAY_LABELS[selectedDomain].toUpperCase()} WHOLENESS
                      </Text>

                      {subdomainData[selectedDomain as THealthDomain].combined.value === 0 ? (
                        <Text variant={'urbanistSemiBoldRegular'}>No change</Text>
                      ) : (
                        <HStack>
                          {subdomainData[selectedDomain as THealthDomain].combined.trend ===
                          WHOLENESS_TREND.IMPROVING ? (
                            <ArrowIconUp />
                          ) : (
                            <ArrowIconDown />
                          )}
                          <Text variant={'urbanistSemiBoldRegular'}>
                            Combined {selectedDomain} wholeness{' '}
                            {subdomainData[selectedDomain].combined.trend === WHOLENESS_TREND.IMPROVING
                              ? 'improved'
                              : 'declined'}{' '}
                            {subdomainData[selectedDomain as THealthDomain].combined.value}% {TIME_STRING}
                          </Text>
                        </HStack>
                      )}
                    </VStack>
                  )}
                </VStack>
              ) : (
                // Wholeness Overview (no Selected Domain)
                <VStack width={'100%'} alignItems={'start'} gap={'24px'}>
                  <VStack alignItems={'start'} gap={'10px'}>
                    <Text variant={'urbanistBold20'} color={'text.darkBlue'} marginTop={'4px'}>
                      Wholeness Overview
                    </Text>
                    <Text variant={'urbanistMedium'} color={'text.mediumGray'}>
                      This is an overview of your current wholeness.
                    </Text>
                  </VStack>

                  {/* Wholeness Score */}
                  <VStack alignItems={'start'} gap={ITEMS_GAP_PX}>
                    <Text variant={'urbanistMaximumBold12'}>WHOLENESS SCORE</Text>
                    <HStack>
                      {combinedWholenessImproving ? <ArrowIconUp /> : <ArrowIconDown />}
                      <Text variant={'urbanistSemiBoldRegular'}>
                        Your wholeness {combinedWholenessImproving ? 'improved' : 'declined'}{' '}
                        {perDomainInsights?.combined.value}% {perDomainInsights?.combined.time}
                      </Text>
                    </HStack>
                  </VStack>

                  {/* Improving Domains */}
                  {Object.entries(perDomainInsights?.improving ?? {}).length && (
                    <VStack alignItems={'start'} gap={ITEMS_GAP_PX}>
                      <Text variant={'urbanistMaximumBold12'}>IMPROVING DOMAINS</Text>
                      <VStack alignItems={'start'} gap={ITEMS_GAP_PX}>
                        {Object.entries(perDomainInsights?.improving ?? {}).map(([domain, value]) => (
                          <HStack key={domain} justify={'start'} width={'100%'}>
                            <ArrowIconUp />
                            <Text variant={'urbanistSemiBoldRegular'}>
                              Your {WELLNESS_DOMAINS_DISPLAY_LABELS[domain as THealthDomain]} improved by {value}%{' '}
                              {TIME_STRING}.
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    </VStack>
                  )}

                  {/* Declining Domains */}
                  {Object.entries(perDomainInsights?.declining ?? {}).length && (
                    <VStack alignItems={'start'} gap={ITEMS_GAP_PX}>
                      <Text variant={'urbanistMaximumBold12'}>DECLINING DOMAINS</Text>
                      <VStack alignItems={'start'} gap={ITEMS_GAP_PX}>
                        {Object.entries(perDomainInsights?.declining ?? {}).map(([domain, value]) => (
                          <HStack key={domain} justify={'start'} width={'100%'}>
                            <ArrowIconDown />
                            <Text variant={'urbanistSemiBoldRegular'}>
                              Your {WELLNESS_DOMAINS_DISPLAY_LABELS[domain as THealthDomain]} declined by {value}%{' '}
                              {TIME_STRING}.
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    </VStack>
                  )}
                </VStack>
              )}
            </VStack>

            {/* White shadow above the button */}
            <Center width={'100%'}>
              <Center width={'85%'} boxShadow={'0px -10px 30px 45px #fff'} borderRadius={'50%'}></Center>
            </Center>

            {overviewBottomButtonOnClick ? (
              <CustomButton
                label={overviewBottomButtonLabel}
                onClick={overviewBottomButtonOnClick}
                iconBeforeLabelSvgPath={overviewBottomButtonIconPath}
                iconBeforeLabelSvgSize={overviewBottomButtonIconSize}
                iconBeforeLabelSvgColor={colors.text.darkBlue}
                labelColor={colors.text.darkBlue}
                hoverBackgroundColor="background.lightBlue"
                borderColor={colors.text.mediumBlue}
                borderHoverWidth="3px"
                width={'100%'}
              />
            ) : (
              <VStack height={'50px'} />
            )}
          </>
        )}
      </VStack>
    </HStack>
  );
};

export default WholenessRingWithOverview;
